import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, InputAdornment, TextField, Typography,
} from '@mui/material';
import { wealthService, apiErrorMessage } from '../../utils/api';

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const MOBILE_REGEX = /^[6-9][0-9]{9}$/;

const toTenDigits = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits.slice(0, 10);
};

/**
 * Collects mobile + PAN before a connect. Mobile is prefilled with the login number and PAN
 * with the one saved earlier; both stay editable. onSubmit({ mobile, pan }) starts the connect
 * and should throw on failure (the error is shown here).
 */
function ConnectDetailsDialog({ open, title, description, defaultMobile, memberId = null, onClose, onSubmit }) {
  const [mobile, setMobile] = useState('');
  const [pan, setPan] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setTouched(false);
    setMobile(toTenDigits(defaultMobile));
    setPan('');
    wealthService.getSavedPan(memberId)
      .then((res) => { if (res?.pan) setPan(res.pan); })
      .catch(() => {});
  }, [open, defaultMobile, memberId]);

  const mobileValid = MOBILE_REGEX.test(mobile);
  const panValid = PAN_REGEX.test(pan);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!mobileValid || !panValid) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({ mobile, pan });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not start the connection. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{description}</Typography>
          <TextField
            fullWidth
            label="Mobile number"
            value={mobile}
            onChange={(e) => setMobile(toTenDigits(e.target.value))}
            onBlur={() => setTouched(true)}
            error={touched && !mobileValid}
            helperText={touched && !mobileValid ? 'Enter a valid 10-digit mobile number' : ' '}
            InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
            inputProps={{ inputMode: 'numeric', autoComplete: 'tel-national', 'aria-label': 'Mobile number' }}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            label="PAN"
            value={pan}
            onChange={(e) => setPan(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
            onBlur={() => setTouched(true)}
            error={touched && !panValid}
            helperText={touched && !panValid ? 'Enter a valid 10-character PAN (e.g. ABCDE1234F)' : ' '}
            inputProps={{ autoCapitalize: 'characters', autoComplete: 'off', 'aria-label': 'PAN number' }}
          />
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={submitting} color="inherit">Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#222' } }}
          >
            {submitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" /> Starting…
              </Box>
            ) : 'Continue'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default ConnectDetailsDialog;
